import React, { Fragment, memo, useCallback, useState } from 'react'
import { useFormikContext } from 'formik'
import { ReviewDecisionBar } from 'src/components/document-review/review/ReviewDecisionBar'
import { AdminApproveReviewStep, AdminRecollectReviewStep } from 'src/constants/actionTypes'
import { InternalPermission } from 'src/constants/internal-access/internalPermissions'
import { useAsyncDispatchWithErrorToast } from 'src/hooks/useAsyncDispatchWithErrorToast'
import { useHasPermission } from 'src/hooks/useHasPermission'
import type {
  DocumentReviewStep,
  ReviewFormValues,
  ReviewRejectionReason,
} from 'src/interfaces/documentReview'
import { resolveApproveBlockReason } from 'src/utils/documentReviewApproval'
import { isTemporaryDriverLicense } from 'src/utils/documentReviewForm'
import { buildApproveStepPayload, buildRecollectStepPayload } from 'src/utils/documentReviewPayload'
import { showToast } from 'src/utils/toast'
import { ApproveStepModal } from './ApproveStepModal'
import { RecollectStepModal } from './RecollectStepModal'

interface Props {
  step: DocumentReviewStep
  reasons: ReviewRejectionReason[]
  reviewedByUser: string
  isSubmitting: boolean
  /** Refetches the queue and advances past this step. Called only after a successful decision. */
  onDecided(): void
  /** Advances past this step without sending any request. */
  onSkip(): void
}

/**
 * The Approve / Recollect / Skip controls for the contractor review screen, plus their confirm
 * modals. Lives inside `ContractorReviewForm` (via `useFormikContext`) so the gate and the
 * approve payload both read the reviewer's current, unsaved edits — not the values the form
 * loaded with.
 */
const ContractorDecisionSectionComponent = ({
  step,
  reasons,
  reviewedByUser,
  isSubmitting,
  onDecided,
  onSkip,
}: Props) => {
  const { values } = useFormikContext<ReviewFormValues>()
  const { asyncDispatchWithErrorToast } = useAsyncDispatchWithErrorToast()
  // POST …/approve-step and …/recollect-step are separate grants (ENG-1207 part 2).
  const canApprove = useHasPermission(InternalPermission.DocumentReviewApprove)
  const canRecollect = useHasPermission(InternalPermission.DocumentReviewRecollect)

  const [isApproveModalOpen, setIsApproveModalOpen] = useState<boolean>(false)
  const [isRecollectModalOpen, setIsRecollectModalOpen] = useState<boolean>(false)

  const blockReason = resolveApproveBlockReason({
    stepType: step.type,
    documentCount: step.partnerDriverCollectedData.documents.length,
    expiresAt: values.expiresAt,
    effectiveAt: values.effectiveAt,
    isExpirationDateRequired: step.isExpirationDateRequired,
    isTemporaryDriverLicense: isTemporaryDriverLicense(step),
    temporaryLicenseValues: values,
  })

  const handleOpenApproveModal = useCallback(() => {
    setIsApproveModalOpen(true)
  }, [])

  const handleCloseApproveModal = useCallback(() => {
    setIsApproveModalOpen(false)
  }, [])

  const handleOpenRecollectModal = useCallback(() => {
    setIsRecollectModalOpen(true)
  }, [])

  const handleCloseRecollectModal = useCallback(() => {
    setIsRecollectModalOpen(false)
  }, [])

  const handleSkip = useCallback(() => {
    showToast('Document skipped')
    onSkip()
  }, [onSkip])

  const handleApproveConfirm = useCallback(async () => {
    const result = await asyncDispatchWithErrorToast(
      AdminApproveReviewStep.request(buildApproveStepPayload({ step, values, reviewedByUser })),
    )

    // A failed dispatch already surfaced its own error toast (asyncDispatchWithErrorToast) — the
    // reviewer stays on this document instead of being advanced past one that was never actually
    // approved. `result` resolves `undefined` on success here (the action carries no success
    // payload) and `null` only on failure, so `null` is the one case to guard.
    if (result === null) {
      return
    }

    setIsApproveModalOpen(false)
    showToast('Document approved', { variant: 'success' })
    onDecided()
  }, [asyncDispatchWithErrorToast, onDecided, reviewedByUser, step, values])

  const handleRecollectConfirm = useCallback(
    async (reasonId: string, reasonMessage: string, reasonDetails: string) => {
      const result = await asyncDispatchWithErrorToast(
        AdminRecollectReviewStep.request(
          buildRecollectStepPayload({
            step,
            values,
            reviewedByUser,
            reasonId,
            reasonMessage,
            reasonDetails,
          }),
        ),
      )

      if (result === null) {
        return
      }

      setIsRecollectModalOpen(false)
      showToast('Recollect requested', { variant: 'success' })
      onDecided()
    },
    [asyncDispatchWithErrorToast, onDecided, reviewedByUser, step, values],
  )

  return (
    <Fragment>
      <ReviewDecisionBar
        blockReason={blockReason}
        isSubmitting={isSubmitting}
        primaryLabel="Approve"
        secondaryLabel="Recollect"
        isApproveVisible={canApprove}
        isSecondaryVisible={canRecollect}
        onApprove={handleOpenApproveModal}
        onSecondary={handleOpenRecollectModal}
        onSkip={handleSkip}
      />
      <ApproveStepModal
        isOpen={isApproveModalOpen}
        expiresAt={values.expiresAt}
        isSubmitting={isSubmitting}
        onConfirm={handleApproveConfirm}
        onClose={handleCloseApproveModal}
      />
      <RecollectStepModal
        isOpen={isRecollectModalOpen}
        reasons={reasons}
        isSubmitting={isSubmitting}
        onConfirm={handleRecollectConfirm}
        onClose={handleCloseRecollectModal}
      />
    </Fragment>
  )
}

export const ContractorDecisionSection = memo(ContractorDecisionSectionComponent)
